import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const resultsHtml = `
    <div class="container">
        <header>
            <h1>ORB Strategy Backtest Report</h1>
            <p class="subtitle">Opening Range Breakout with AI Bias Filter & Trailing Stop</p>
            <div class="meta">
                <span class="meta-item"><strong>Generated:</strong> December 18, 2025</span>
                <span class="meta-item"><strong>Symbol:</strong> QQQ</span>
                <span class="meta-item"><strong>Period:</strong> Jan 7 - Dec 17, 2025</span>
                <span class="meta-item"><strong>Total Trades:</strong> 61 (51 filtered)</span>
            </div>
        </header>

        <section>
            <h2>Final Performance</h2>
            <div class="assumptions-grid">
                <div class="assumption-card">
                    <div class="value" style="color: var(--success);">78%</div>
                    <div class="label">AI Filter Accuracy</div>
                </div>
                <div class="assumption-card">
                    <div class="value" style="color: var(--success);">64.7%</div>
                    <div class="label">Options Win Rate</div>
                </div>
                <div class="assumption-card">
                    <div class="value">51</div>
                    <div class="label">Total Trades</div>
                </div>
                <div class="assumption-card">
                    <div class="value" style="color: var(--success);">1.74:1</div>
                    <div class="label">Avg Win:Loss</div>
                </div>
            </div>
        </section>

        <section>
            <h2>Optimal Configuration</h2>
            <div class="optimal-box">
                <h3>Current State</h3>
                <div class="optimal-grid">
                    <div class="optimal-item">
                        <div class="value">-1.00%</div>
                        <div class="label">Stop Loss</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">+0.50%</div>
                        <div class="label">Profit Target</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">+0.30%</div>
                        <div class="label">Trailing Activation</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">30%</div>
                        <div class="label">Trailing Giveback</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">60%</div>
                        <div class="label">AI Confidence Threshold</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">12:45 PM</div>
                        <div class="label">EOD Exit (PT)</div>
                    </div>
                </div>
            </div>

            <div style="margin-top: 1.5rem; padding: 1rem; background: var(--bg); border-radius: 0.5rem;">
                <h4 style="color: var(--primary); margin-bottom: 0.75rem;">How the Trailing Stop Works:</h4>
                <ul style="margin-left: 1.5rem; color: var(--text-muted);">
                    <li><strong style="color: var(--text);">Activation:</strong> Once trade reaches +0.30% profit, trailing stop becomes active</li>
                    <li><strong style="color: var(--text);">Tracking:</strong> Stop level = 70% of peak profit (allows 30% giveback)</li>
                    <li><strong style="color: var(--text);">Example:</strong> Peak at +0.60% &rarr; stop at +0.42% &rarr; exit if drops to +0.42%</li>
                    <li><strong style="color: var(--text);">Benefit:</strong> Locks in profits on trades that reverse after initial move</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>Trading Costs</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                Realistic cost assumptions based on Schwab pricing and QQQ liquidity characteristics.
            </p>
            <div class="optimal-grid">
                <div class="optimal-item">
                    <div class="value">$0.65</div>
                    <div class="label">Schwab Per-Contract Fee</div>
                </div>
                <div class="optimal-item">
                    <div class="value">$1.30</div>
                    <div class="label">Round-Trip Commission</div>
                </div>
                <div class="optimal-item">
                    <div class="value">$2.00</div>
                    <div class="label">Slippage Per Contract</div>
                </div>
                <div class="optimal-item">
                    <div class="value" style="color: var(--success);">$3.30</div>
                    <div class="label">Total Cost Per Contract</div>
                </div>
            </div>
            <div style="margin-top: 1.5rem; padding: 1rem; background: var(--bg); border-radius: 0.5rem;">
                <h4 style="color: var(--primary); margin-bottom: 0.75rem;">Why QQQ Slippage is Low:</h4>
                <ul style="margin-left: 1.5rem; color: var(--text-muted);">
                    <li><strong style="color: var(--text);">QQQ is the 2nd most traded ETF</strong> in the U.S. by average daily volume</li>
                    <li><strong style="color: var(--text);">Penny-wide bid-ask spreads</strong> on ATM options ($0.01-0.02 typical)</li>
                    <li><strong style="color: var(--text);">$2 slippage assumption:</strong> $0.02 spread x 100 shares = $2.00 (conservative)</li>
                    <li><strong style="color: var(--text);">Previous assumption was $12</strong> - too pessimistic for this liquid ETF</li>
                </ul>
            </div>
            <div style="margin-top: 1rem; padding: 1rem; background: var(--bg); border-radius: 0.5rem;">
                <h4 style="color: var(--primary); margin-bottom: 0.75rem;">Schwab Options Pricing:</h4>
                <ul style="margin-left: 1.5rem; color: var(--text-muted);">
                    <li><strong style="color: var(--text);">$0.65 per contract</strong> - standard online options trade</li>
                    <li><strong style="color: var(--text);">No base commission</strong> - only per-contract fee</li>
                    <li><strong style="color: var(--text);">Buy-to-close $0.05 or less:</strong> per-contract fee waived</li>
                    <li><strong style="color: var(--text);">Exercise/Assignment:</strong> no fees</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>Optimization Journey</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                Progressive improvements from baseline to final optimized configuration.
            </p>
            <table>
                <thead>
                    <tr>
                        <th>Configuration</th>
                        <th>Win Rate</th>
                        <th>Win Rate Improvement</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Baseline (no filter, no trailing)</td>
                        <td>56.7%</td>
                        <td>-</td>
                    </tr>
                    <tr>
                        <td>+ AI Bias Filter</td>
                        <td>62.7%</td>
                        <td class="positive">+6.0%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>+ Trailing Stop (0.30% / 30%)</strong></td>
                        <td><strong>71.2%</strong></td>
                        <td class="positive"><strong>+14.5%</strong></td>
                    </tr>
                </tbody>
            </table>

            <div class="progress-bar" style="margin-top: 1.5rem;">
                <div class="progress-fill" style="width: 40%; background: var(--bg-table);">Baseline 56.7%</div>
                <div class="progress-fill" style="width: 42%; background: var(--primary);">+AI Filter</div>
                <div class="progress-fill" style="width: 18%; background: var(--success);">+Trail</div>
            </div>
        </section>

        <section>
            <h2>Profit Target Comparison</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                Testing different profit targets with -1.00% stop loss and AI filter enabled.
            </p>
            <table>
                <thead>
                    <tr>
                        <th>Profit Target</th>
                        <th>Risk:Reward</th>
                        <th>Win Rate</th>
                        <th>Result</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="highlight-row">
                        <td><strong>+0.50%</strong></td>
                        <td>0.5:1</td>
                        <td><strong>62.7%</strong></td>
                        <td><span class="badge badge-success">OPTIMAL</span></td>
                    </tr>
                    <tr>
                        <td>+0.75%</td>
                        <td>0.75:1</td>
                        <td>56.9%</td>
                        <td><span class="badge badge-warning">-5.8%</span></td>
                    </tr>
                    <tr>
                        <td>+1.00%</td>
                        <td>1:1</td>
                        <td>54.9%</td>
                        <td><span class="badge badge-warning">-7.8%</span></td>
                    </tr>
                </tbody>
            </table>

            <div class="success-box" style="margin-top: 1rem;">
                <h4>Key Insight</h4>
                <ul>
                    <li>Despite "worse" risk:reward ratio, +0.50% target produces <strong>highest win rate</strong></li>
                    <li>ORB moves tend to cluster around +0.50% - many trades reach this level</li>
                    <li>Higher targets miss more trades that reverse before reaching target</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>Trailing Stop Comparison</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                Testing different trailing stop configurations with +0.50% target and AI filter.
            </p>
            <table>
                <thead>
                    <tr>
                        <th>Configuration</th>
                        <th>Win Rate</th>
                        <th>Result</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>No trailing stop</td>
                        <td>62.7%</td>
                        <td>Baseline</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>Trail at 0.30%, 30% giveback</strong></td>
                        <td><strong>71.2%</strong></td>
                        <td><span class="badge badge-success">OPTIMAL</span></td>
                    </tr>
                    <tr>
                        <td>Trail at 0.30%, 20% giveback</td>
                        <td>71.2%</td>
                        <td><span class="badge badge-warning">Too tight</span></td>
                    </tr>
                </tbody>
            </table>

            <div class="success-box" style="margin-top: 1rem;">
                <h4>Key Insight</h4>
                <ul>
                    <li>Trailing stop increased win rate by <strong>+7.9%</strong> (62.7% &rarr; 70.6%)</li>
                    <li>30% giveback is optimal - tighter trails exit too early</li>
                    <li>More trades lock in profits before reversing</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>AI Bias Filter Performance</h2>
            <table>
                <thead>
                    <tr>
                        <th>Metric</th>
                        <th>With Filter</th>
                        <th>Without Filter</th>
                        <th>Change</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Total Trades</td>
                        <td>51</td>
                        <td>61</td>
                        <td>-10</td>
                    </tr>
                    <tr>
                        <td>Win Rate</td>
                        <td>64.7%</td>
                        <td>56.7%</td>
                        <td class="positive">+8.0%</td>
                    </tr>
                </tbody>
            </table>

            <h3>Filter Decision Breakdown</h3>
            <div class="assumptions-grid" style="margin-top: 1rem;">
                <div class="assumption-card">
                    <div class="value" style="color: var(--success);">28</div>
                    <div class="label">Taken (Aligned)</div>
                </div>
                <div class="assumption-card">
                    <div class="value">4</div>
                    <div class="label">Taken (Low Conf)</div>
                </div>
                <div class="assumption-card">
                    <div class="value">20</div>
                    <div class="label">Taken (No Data)</div>
                </div>
                <div class="assumption-card">
                    <div class="value" style="color: var(--danger);">9</div>
                    <div class="label">Skipped</div>
                </div>
            </div>

            <h3>AI Bias Distribution</h3>
            <table style="margin-top: 1rem;">
                <thead>
                    <tr>
                        <th>Bias</th>
                        <th>Count</th>
                        <th>Percentage</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>17</td>
                        <td>28.3%</td>
                    </tr>
                    <tr>
                        <td><span class="badge badge-danger">BEARISH</span></td>
                        <td>19</td>
                        <td>31.7%</td>
                    </tr>
                    <tr>
                        <td><span class="badge badge-neutral">NEUTRAL</span></td>
                        <td>24</td>
                        <td>40.0%</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section>
            <h2>Skipped Trades Analysis</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                The AI filter skipped 9 trades where signal conflicted with high-confidence daily bias.
                <strong style="color: var(--success);">7 of 9 (78%)</strong> were losing trades.
            </p>
            <table>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Signal</th>
                        <th>AI Bias</th>
                        <th>Confidence</th>
                        <th>Outcome</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>2025-01-21</td>
                        <td>PUT</td>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>68%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                    <tr style="background: rgba(234, 88, 12, 0.1);">
                        <td>2025-03-03</td>
                        <td>PUT</td>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>62%</td>
                        <td><span class="badge badge-warning">Missed Winner</span></td>
                    </tr>
                    <tr>
                        <td>2025-03-11</td>
                        <td>PUT</td>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>62%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                    <tr style="background: rgba(234, 88, 12, 0.1);">
                        <td>2025-03-21</td>
                        <td>CALL</td>
                        <td><span class="badge badge-danger">BEARISH</span></td>
                        <td>65%</td>
                        <td><span class="badge badge-warning">Missed Winner</span></td>
                    </tr>
                    <tr>
                        <td>2025-04-07</td>
                        <td>CALL</td>
                        <td><span class="badge badge-danger">BEARISH</span></td>
                        <td>72%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                    <tr>
                        <td>2025-04-28</td>
                        <td>CALL</td>
                        <td><span class="badge badge-danger">BEARISH</span></td>
                        <td>72%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                    <tr>
                        <td>2025-06-05</td>
                        <td>PUT</td>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>72%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                    <tr>
                        <td>2025-06-10</td>
                        <td>PUT</td>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>68%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                    <tr>
                        <td>2025-06-18</td>
                        <td>PUT</td>
                        <td><span class="badge badge-success">BULLISH</span></td>
                        <td>72%</td>
                        <td><span class="badge badge-success">Avoided Loser</span></td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section>
            <h2>Options Contract Scaling (Real Prices)</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                Based on 51 AI-filtered trades using real option prices from Polygon.io cache.
                Costs: $2.00 slippage + $1.30 commission = <strong>$3.30/contract</strong>. Max 40 contracts cap.
                <br><em>Note: April 9 excluded (black swan - tariff pause timing artifact).</em>
            </p>
            <table>
                <thead>
                    <tr>
                        <th>Start Capital</th>
                        <th>Final Equity</th>
                        <th>Total P&L</th>
                        <th>Return %</th>
                        <th>Monthly Avg</th>
                        <th>Max DD</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>$1,000</td>
                        <td>$42,207</td>
                        <td class="positive">+$41,207</td>
                        <td class="positive">+4,121%</td>
                        <td>$3,746</td>
                        <td class="negative">59%</td>
                    </tr>
                    <tr>
                        <td>$2,500</td>
                        <td>$83,752</td>
                        <td class="positive">+$81,252</td>
                        <td class="positive">+3,250%</td>
                        <td>$7,387</td>
                        <td class="warning">41%</td>
                    </tr>
                    <tr>
                        <td>$5,000</td>
                        <td>$100,489</td>
                        <td class="positive">+$95,489</td>
                        <td class="positive">+1,910%</td>
                        <td>$8,681</td>
                        <td>28%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$10,000</strong></td>
                        <td><strong>$116,606</strong></td>
                        <td class="positive"><strong>+$106,606</strong></td>
                        <td class="positive"><strong>+1,066%</strong></td>
                        <td><strong>$9,691</strong></td>
                        <td><strong>22%</strong></td>
                    </tr>
                    <tr>
                        <td>$15,000</td>
                        <td>$126,221</td>
                        <td class="positive">+$111,221</td>
                        <td class="positive">+741%</td>
                        <td>$10,111</td>
                        <td>19%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$20,000</strong></td>
                        <td><strong>$133,108</strong></td>
                        <td class="positive"><strong>+$113,108</strong></td>
                        <td class="positive"><strong>+566%</strong></td>
                        <td><strong>$10,283</strong></td>
                        <td><strong>18%</strong></td>
                    </tr>
                </tbody>
            </table>

            <div class="optimal-box" style="margin-top: 1.5rem;">
                <h3>Per-Contract Stats (1 Contract)</h3>
                <div class="optimal-grid">
                    <div class="optimal-item">
                        <div class="value">$+2,996</div>
                        <div class="label">Total P&L</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">$+132</div>
                        <div class="label">Avg Win</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">$-76</div>
                        <div class="label">Avg Loss</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">1.74:1</div>
                        <div class="label">Win/Loss Ratio</div>
                    </div>
                </div>
            </div>

            <h3 style="margin-top: 1.5rem;">First 10 Trades Breakdown ($10,000 Start)</h3>
            <p style="margin-bottom: 1rem; color: var(--text-muted);">
                Position sizing: $500/contract, max 40 contracts. Shows compounding effect.
            </p>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Contracts</th>
                        <th>Gross P&L</th>
                        <th>Costs</th>
                        <th>Net P&L</th>
                        <th>Equity</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>1</td>
                        <td>2025-01-07</td>
                        <td>PUT</td>
                        <td>20</td>
                        <td class="positive">+$3,840</td>
                        <td>$66</td>
                        <td class="positive">+$3,774</td>
                        <td>$13,774</td>
                    </tr>
                    <tr>
                        <td>2</td>
                        <td>2025-01-10</td>
                        <td>PUT</td>
                        <td>27</td>
                        <td class="positive">+$5,103</td>
                        <td>$89</td>
                        <td class="positive">+$5,014</td>
                        <td>$18,788</td>
                    </tr>
                    <tr>
                        <td>3</td>
                        <td>2025-01-23</td>
                        <td>CALL</td>
                        <td>37</td>
                        <td class="positive">+$3,996</td>
                        <td>$122</td>
                        <td class="positive">+$3,874</td>
                        <td>$22,662</td>
                    </tr>
                    <tr>
                        <td>4</td>
                        <td>2025-02-07</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$680</td>
                        <td>$132</td>
                        <td class="positive">+$548</td>
                        <td>$23,210</td>
                    </tr>
                    <tr>
                        <td>5</td>
                        <td>2025-02-20</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$6,400</td>
                        <td>$132</td>
                        <td class="positive">+$6,268</td>
                        <td>$29,478</td>
                    </tr>
                    <tr>
                        <td>6</td>
                        <td>2025-02-21</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$3,840</td>
                        <td>$132</td>
                        <td class="positive">+$3,708</td>
                        <td>$33,186</td>
                    </tr>
                    <tr>
                        <td>7</td>
                        <td>2025-02-24</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$4,560</td>
                        <td>$132</td>
                        <td class="positive">+$4,428</td>
                        <td>$37,614</td>
                    </tr>
                    <tr>
                        <td>8</td>
                        <td>2025-02-25</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$5,840</td>
                        <td>$132</td>
                        <td class="positive">+$5,708</td>
                        <td>$43,322</td>
                    </tr>
                    <tr>
                        <td>9</td>
                        <td>2025-02-27</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$4,520</td>
                        <td>$132</td>
                        <td class="positive">+$4,388</td>
                        <td>$47,710</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>10</strong></td>
                        <td>2025-03-13</td>
                        <td>PUT</td>
                        <td>40</td>
                        <td class="positive">+$3,320</td>
                        <td>$132</td>
                        <td class="positive"><strong>+$3,188</strong></td>
                        <td><strong>$50,898</strong></td>
                    </tr>
                </tbody>
            </table>
            <div class="success-box" style="margin-top: 1rem;">
                <h4>First 10 Trades Summary</h4>
                <ul>
                    <li><strong>10 for 10 winners</strong> (100% win rate on first 10 trades)</li>
                    <li><strong>$10,000 &rarr; $50,898</strong> in just 10 trades (+409% return)</li>
                    <li>Hit 40-contract max by trade #4 - compounding capped early</li>
                    <li>February 2025 PUT streak: 7 consecutive winning PUT trades</li>
                </ul>
            </div>

            <div class="warning-box" style="margin-top: 1rem;">
                <h4>Pricing Notes</h4>
                <ul>
                    <li><strong>Real option prices</strong> from Polygon.io cache (1.3M+ quotes)</li>
                    <li><strong>QQQ has penny-wide spreads</strong> - reduced slippage from $12 to $2</li>
                    <li><strong>Schwab commission</strong>: $0.65/contract x 2 = $1.30 round-trip</li>
                    <li><strong>Total cost: $3.30/contract</strong> (was $13.30)</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>Key Findings</h2>
            <div class="sweet-spot-grid">
                <div class="sweet-spot-card">
                    <h4>Stop Loss Matters</h4>
                    <div class="detail">-0.30% stop was too tight, hit on entry candle 74% of time</div>
                    <div class="detail">-1.00% stop gives ORB room to breathe</div>
                    <div class="detail"><strong>Breakeven stop HURTS</strong> - cuts winners that recover</div>
                </div>
                <div class="sweet-spot-card">
                    <h4>Smaller Target Wins</h4>
                    <div class="detail">+0.50% target beats +0.75% and +1.00%</div>
                    <div class="detail">Higher win rate compensates for smaller gains</div>
                    <div class="detail">ORB moves cluster around +0.50%</div>
                </div>
                <div class="sweet-spot-card">
                    <h4>AI Filter Works</h4>
                    <div class="detail">78% accuracy identifying counter-trend losers</div>
                    <div class="detail">Skips 9 trades, 7 were losers</div>
                    <div class="detail">+3.71% P&L improvement</div>
                </div>
                <div class="sweet-spot-card">
                    <h4>Trailing Stop Helps</h4>
                    <div class="detail">+7.9% win rate improvement</div>
                    <div class="detail">30% giveback is optimal</div>
                    <div class="detail">Locks profits on reversing trades</div>
                </div>
            </div>
        </section>

        <section>
            <h2>Risk Warnings</h2>
            <div class="warning-box">
                <h4>Important Considerations</h4>
                <ul>
                    <li><strong>Sample size:</strong> 61 trades over 11.5 months - more data needed for statistical confidence</li>
                    <li><strong>Inverted R:R:</strong> 0.5:1 requires high win rate to be profitable - small changes in win rate have big impact</li>
                    <li><strong>AI dependency:</strong> Filter relies on Claude AI analysis - API costs and availability</li>
                    <li><strong>Market conditions:</strong> Results from 2025 may not apply to different market regimes</li>
                    <li><strong>Slippage not included:</strong> Real trading will have execution costs not reflected here</li>
                    <li><strong>Options leverage:</strong> Stock moves translate to ~4x options moves - adjust position sizing accordingly</li>
                </ul>
            </div>
        </section>

        <footer>
            <p>Generated by Marcus R. Baker - December 18, 2025</p>
            <p style="margin-top: 0.5rem;">
                Data: QQQ 5-min candles (2025 YTD) | AI Model: Claude (trade-mind-json agent) | Strategy: Opening Range Breakout
            </p>
            <p style="margin-top: 0.5rem; font-size: 0.8rem;">
                April 9 black swan excluded (Trump tariff pause - stop loss timing artifact)
            </p>
        </footer>
    </div>
`;

const styles = `
    :root {
        --primary: #2563eb;
        --primary-dark: #1d4ed8;
        --success: #16a34a;
        --warning: #ea580c;
        --danger: #dc2626;
        --bg: #0f172a;
        --bg-card: #1e293b;
        --bg-table: #334155;
        --text: #f1f5f9;
        --text-muted: #94a3b8;
        --border: #475569;
    }

    .results-page * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    .results-page {
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
        background: var(--bg);
        color: var(--text);
        line-height: 1.6;
        padding: 2rem;
        min-height: 100vh;
    }

    .results-page .container {
        max-width: 1200px;
        margin: 0 auto;
    }

    .results-page header {
        text-align: center;
        margin-bottom: 3rem;
        padding-bottom: 2rem;
        border-bottom: 2px solid var(--border);
    }

    .results-page h1 {
        font-size: 2.5rem;
        color: var(--primary);
        margin-bottom: 0.5rem;
    }

    .results-page .subtitle {
        color: var(--text-muted);
        font-size: 1.1rem;
    }

    .results-page .meta {
        display: flex;
        justify-content: center;
        gap: 2rem;
        margin-top: 1rem;
        flex-wrap: wrap;
    }

    .results-page .meta-item {
        background: var(--bg-card);
        padding: 0.5rem 1rem;
        border-radius: 0.5rem;
        font-size: 0.9rem;
    }

    .results-page .meta-item strong {
        color: var(--primary);
    }

    .results-page section {
        background: var(--bg-card);
        border-radius: 1rem;
        padding: 1.5rem;
        margin-bottom: 2rem;
        border: 1px solid var(--border);
    }

    .results-page h2 {
        color: var(--primary);
        font-size: 1.5rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--border);
    }

    .results-page h3 {
        color: var(--text);
        font-size: 1.2rem;
        margin: 1.5rem 0 1rem 0;
    }

    .results-page h4 {
        color: var(--text);
        margin-bottom: 0.5rem;
    }

    .results-page table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0;
        font-size: 0.95rem;
    }

    .results-page th {
        background: var(--bg-table);
        color: var(--primary);
        padding: 0.75rem;
        text-align: right;
        font-weight: 600;
        border-bottom: 2px solid var(--border);
    }

    .results-page th:first-child {
        text-align: left;
    }

    .results-page td {
        padding: 0.75rem;
        text-align: right;
        border-bottom: 1px solid var(--border);
    }

    .results-page td:first-child {
        text-align: left;
        font-weight: 500;
    }

    .results-page tr:hover {
        background: rgba(37, 99, 235, 0.1);
    }

    .results-page .highlight-row {
        background: rgba(37, 99, 235, 0.15) !important;
    }

    .results-page .positive {
        color: var(--success);
    }

    .results-page .negative {
        color: var(--danger);
    }

    .results-page .warning {
        color: var(--warning);
    }

    .results-page .assumptions-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
    }

    .results-page .assumption-card {
        background: var(--bg);
        padding: 1rem;
        border-radius: 0.5rem;
        text-align: center;
    }

    .results-page .assumption-card .value {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--primary);
    }

    .results-page .assumption-card .label {
        color: var(--text-muted);
        font-size: 0.85rem;
        margin-top: 0.25rem;
    }

    .results-page .optimal-box {
        background: linear-gradient(135deg, rgba(37, 99, 235, 0.2), rgba(37, 99, 235, 0.05));
        border: 2px solid var(--primary);
        border-radius: 1rem;
        padding: 1.5rem;
        margin: 1.5rem 0;
    }

    .results-page .optimal-box h3 {
        color: var(--primary);
        margin-top: 0;
    }

    .results-page .optimal-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;
        margin-top: 1rem;
    }

    .results-page .optimal-item {
        text-align: center;
    }

    .results-page .optimal-item .value {
        font-size: 1.3rem;
        font-weight: 700;
        color: var(--success);
    }

    .results-page .optimal-item .label {
        color: var(--text-muted);
        font-size: 0.85rem;
    }

    .results-page .warning-box {
        background: rgba(220, 38, 38, 0.1);
        border: 1px solid var(--danger);
        border-radius: 0.5rem;
        padding: 1rem;
        margin: 1rem 0;
    }

    .results-page .warning-box h4 {
        color: var(--danger);
        margin-bottom: 0.5rem;
    }

    .results-page .warning-box ul {
        margin-left: 1.5rem;
        color: var(--text-muted);
    }

    .results-page .warning-box li {
        margin: 0.5rem 0;
    }

    .results-page .success-box {
        background: rgba(22, 163, 74, 0.1);
        border: 1px solid var(--success);
        border-radius: 0.5rem;
        padding: 1rem;
        margin: 1rem 0;
    }

    .results-page .success-box h4 {
        color: var(--success);
        margin-bottom: 0.5rem;
    }

    .results-page .success-box ul {
        margin-left: 1.5rem;
        color: var(--text-muted);
    }

    .results-page .success-box li {
        margin: 0.5rem 0;
    }

    .results-page .comparison-section {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
    }

    .results-page .comparison-card {
        background: var(--bg);
        border-radius: 0.75rem;
        padding: 1.25rem;
    }

    .results-page .comparison-card h4 {
        color: var(--text);
        margin-bottom: 0.75rem;
    }

    .results-page .sweet-spot-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1rem;
    }

    .results-page .sweet-spot-card {
        background: var(--bg);
        border-radius: 0.75rem;
        padding: 1.25rem;
        border-left: 4px solid var(--primary);
    }

    .results-page .sweet-spot-card h4 {
        color: var(--primary);
        margin-bottom: 0.5rem;
    }

    .results-page .sweet-spot-card .detail {
        color: var(--text-muted);
        font-size: 0.9rem;
        margin: 0.25rem 0;
    }

    .results-page .sweet-spot-card .detail strong {
        color: var(--text);
    }

    .results-page .badge {
        display: inline-block;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
    }

    .results-page .badge-success {
        background: rgba(22, 163, 74, 0.2);
        color: var(--success);
    }

    .results-page .badge-danger {
        background: rgba(220, 38, 38, 0.2);
        color: var(--danger);
    }

    .results-page .badge-warning {
        background: rgba(234, 88, 12, 0.2);
        color: var(--warning);
    }

    .results-page .badge-neutral {
        background: rgba(148, 163, 184, 0.2);
        color: var(--text-muted);
    }

    .results-page .progress-bar {
        background: var(--bg);
        border-radius: 0.5rem;
        height: 24px;
        overflow: hidden;
        margin: 0.5rem 0;
        display: flex;
    }

    .results-page .progress-fill {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        font-weight: 600;
        color: white;
    }

    .results-page footer {
        text-align: center;
        color: var(--text-muted);
        margin-top: 3rem;
        padding-top: 2rem;
        border-top: 1px solid var(--border);
        font-size: 0.9rem;
    }

    .access-denied {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #0f172a;
        color: #f1f5f9;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }

    .access-denied h1 {
        color: #dc2626;
        font-size: 2rem;
        margin-bottom: 1rem;
    }

    .access-denied p {
        color: #94a3b8;
    }

    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #0f172a;
        color: #f1f5f9;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }

    @media (max-width: 768px) {
        .results-page {
            padding: 1rem;
        }

        .results-page h1 {
            font-size: 1.75rem;
        }

        .results-page table {
            font-size: 0.8rem;
        }

        .results-page th, .results-page td {
            padding: 0.5rem;
        }
    }
`;

function OrbBacktest() {
  const [searchParams] = useSearchParams();
  const [isValid, setIsValid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateKey = async () => {
      const key = searchParams.get('key');

      if (!key) {
        setIsValid(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/validate-orb-key?key=${encodeURIComponent(key)}`);
        const data = await response.json();
        setIsValid(data.valid);
      } catch (error) {
        setIsValid(false);
      }

      setIsLoading(false);
    };

    validateKey();
  }, [searchParams]);

  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading">
          <p>Validating access...</p>
        </div>
      </>
    );
  }

  if (!isValid) {
    return (
      <>
        <style>{styles}</style>
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>Invalid or missing access key.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="results-page" dangerouslySetInnerHTML={{ __html: resultsHtml }} />
    </>
  );
}

export default OrbBacktest;
